import { useEffect, useState } from "react";
import { firms } from "@/api/firms.service";

// In-flight tracking + simple module-scoped cache so repeated calls dedupe
// across rows on the same page. Keys are address-book UUIDs.
const cache = new Map<string, string>();
const inFlight = new Map<string, Promise<string | null>>();

async function fetchOne(uuid: string): Promise<string | null> {
  const cached = cache.get(uuid);
  if (cached) return cached;
  const existing = inFlight.get(uuid);
  if (existing) return existing;

  const promise = firms
    .getByUuid(uuid)
    .then(firm => {
      if (firm) {
        cache.set(uuid, firm.name);
        return firm.name;
      }
      return null;
    })
    .catch(err => {
      // Name resolution is a UI-only nicety used for firm chips; on failure
      // fall back to rendering the raw UUID. No logger is wired into staff-ui
      // yet — log so the failure is at least visible in dev tools.
      console.error("[useCompanyNames] failed to resolve firm name", uuid, err);
      return null;
    })
    .finally(() => {
      inFlight.delete(uuid);
    });
  inFlight.set(uuid, promise);
  return promise;
}

async function fetchBatch(uuids: string[]): Promise<Record<string, string>> {
  const uncached = uuids.filter(id => !cache.has(id) && Boolean(id));

  if (uncached.length > 0) {
    try {
      const batch = await firms.getBatchByUuids(uncached);
      for (const c of batch) {
        if (c.id) cache.set(c.id, c.name);
      }
    } catch (err) {
      // Batch endpoint may not exist or may be rate-limited; per-id calls each
      // have their own catch + cache so the fallback is safe and self-healing.
      console.error("[useCompanyNames] batch lookup failed, falling back to per-id", err);
      await Promise.all(uncached.map(id => fetchOne(id)));
    }
  }

  const result: Record<string, string> = {};
  for (const id of uuids) {
    const name = cache.get(id);
    if (name) result[id] = name;
  }
  return result;
}

/**
 * Resolve a list of company UUIDs to names. Batches the lookup in a single
 * request when possible; otherwise issues N parallel fetches. Cached
 * module-wide so multiple rows in one page share results.
 */
export function useCompanyNames(companyIds: string[]): Record<string, string> {
  const [names, setNames] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const id of companyIds) {
      const cached = cache.get(id);
      if (cached) initial[id] = cached;
    }
    return initial;
  });

  // Stable key so we only re-run when the set of ids actually changes.
  const key = [...new Set(companyIds)].sort().join(",");

  useEffect(() => {
    if (companyIds.length === 0) return;
    let cancelled = false;
    fetchBatch([...new Set(companyIds)]).then(result => {
      if (cancelled) return;
      setNames(prev => ({ ...prev, ...result }));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return names;
}

/** Test-only: clear the module-scoped cache. */
export function __resetCompanyNamesCache() {
  cache.clear();
  inFlight.clear();
}
